import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { SparkAdsStore } from './services/spark-ads-store';
import { SparkAdsView, isSparkAdsView } from './spark-ads.types';
import { AdsDashboard } from './molecules/ads-dashboard/ads-dashboard';
import { CreatorVideos } from './molecules/creator-videos/creator-videos';
import { SparkAdsBuilder } from './molecules/spark-ads-builder/spark-ads-builder';
import { GlossaryDrawer } from './molecules/glossary-drawer/glossary-drawer';

interface SparkAdsNavItem {
  view: SparkAdsView;
  label: string;
}

/**
 * Shell for the Spark Ads experience: the Ads dashboard, the guided builder and the
 * creator video library.
 *
 * Hosted as a Manage Brief tab, so everything here is scoped to one brief — a brief
 * owns exactly one TikTok Spark campaign.
 */
@Component({
  selector: 'mbt-spark-ads',
  imports: [AdsDashboard, CreatorVideos, SparkAdsBuilder, GlossaryDrawer],
  templateUrl: './spark-ads.html',
  styleUrl: './spark-ads.scss',
  host: { class: 'block h-full w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparkAds implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  protected readonly store = inject(SparkAdsStore);
  protected readonly manageBriefTabsService = inject(ManageBriefTabsService);

  protected readonly navItems: readonly SparkAdsNavItem[] = [
    { view: 'dashboard', label: 'Dashboard' },
    { view: 'videos', label: 'Creator videos' },
    { view: 'new', label: 'New Spark ad' },
  ];

  protected readonly briefId = computed(() => this.manageBriefTabsService.focussedBrief()?.id);

  ngOnInit(): void {
    const subTab = this.route.snapshot.params['subTab'];
    if (isSparkAdsView(subTab)) {
      this.store.setView(subTab);
    }
  }

  /**
   * Switches view and keeps the URL deep-linkable.
   *
   * `replaceState` rather than a router navigation: the tab param is read once by the
   * parent on init, so navigating would re-run the brief fetch for no reason.
   */
  protected selectView(view: SparkAdsView): void {
    this.store.setView(view);

    const briefId = this.briefId();
    if (briefId) {
      this.location.replaceState(`/manage-brief/${briefId}/14/${view}`);
    }
  }

  protected getNavClass(view: SparkAdsView): string {
    return this.store.view() === view
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }
}
